import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Input } from "./input";

/**
 * DateInput - Componente per input data con supporto per incolla
 * 
 * Caratteristiche:
 * - Permette l'incolla di date in formato italiano (gg/mm/aaaa)
 * - Smart Parsing: riconosce date senza separatori (12121997 → 12/12/1997)
 * - Valida automaticamente e converte nel formato corretto
 * - Mostra calendario per selezione manuale
 * - Salva in formato ISO (YYYY-MM-DD)
 * 
 * Formati accettati:
 * - ggmmaaaa (es. 12121997 → 12/12/1997) - SMART PARSING
 * - gg/mm/aaaa (es. 23/01/1984)
 * - gg-mm-aaaa (es. 23-01-1984)
 * - gg.mm.aaaa (es. 23.01.1984)
 * - gg/mm/aa (es. 23/01/84 → 23/01/1984)
 * - ggmmaa (es. 230184 → 23/01/1984) - SMART PARSING
 * - aaaa-mm-gg (ISO, es. 1984-01-23)
 * - aaaammgg (es. 19840123 → 23/01/1984) - SMART PARSING ISO
 */
const DateInput = React.forwardRef(({ 
  value, 
  onChange, 
  placeholder = "gg/mm/aaaa",
  className,
  disabled,
  ...props 
}, ref) => {
  // Stato per il valore testuale mostrato all'utente
  const [textValue, setTextValue] = React.useState("");
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  // Funzione per parsare una stringa data in vari formati (SMART PARSING)
  const parseDate = (str) => {
    if (!str) return null;
    
    // Rimuovi spazi e caratteri non validi
    const cleanStr = str.trim().replace(/\s/g, '');
    
    // Formato ISO: YYYY-MM-DD (già valido)
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
      const date = new Date(cleanStr);
      return isValid(date) ? cleanStr : null;
    }
    
    // SMART PARSING: 8 cifre consecutive formato ggmmaaaa (es. 12121997)
    if (/^\d{8}$/.test(cleanStr)) {
      // Prova prima il formato italiano ggmmaaaa
      const dayIt = cleanStr.substring(0, 2);
      const monthIt = cleanStr.substring(2, 4);
      const yearIt = cleanStr.substring(4, 8);
      
      // Verifica se è un formato italiano valido (giorno 01-31, mese 01-12)
      const dayNum = parseInt(dayIt);
      const monthNum = parseInt(monthIt);
      
      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12) {
        const isoDate = `${yearIt}-${monthIt}-${dayIt}`;
        const date = new Date(isoDate);
        if (isValid(date) && date.getDate() === dayNum && date.getMonth() + 1 === monthNum) {
          return isoDate;
        }
      }
      
      // Se non è italiano valido, prova il formato ISO aaaammgg
      const yearIso = cleanStr.substring(0, 4);
      const monthIso = cleanStr.substring(4, 6);
      const dayIso = cleanStr.substring(6, 8);
      
      const dayNumIso = parseInt(dayIso);
      const monthNumIso = parseInt(monthIso);
      
      if (dayNumIso >= 1 && dayNumIso <= 31 && monthNumIso >= 1 && monthNumIso <= 12) {
        const isoDate = `${yearIso}-${monthIso}-${dayIso}`;
        const date = new Date(isoDate);
        if (isValid(date) && date.getDate() === dayNumIso && date.getMonth() + 1 === monthNumIso) {
          return isoDate;
        }
      }
    }
    
    // SMART PARSING: 6 cifre consecutive formato ggmmaa (es. 230184)
    if (/^\d{6}$/.test(cleanStr)) {
      const day = cleanStr.substring(0, 2);
      const month = cleanStr.substring(2, 4);
      const shortYear = cleanStr.substring(4, 6);
      const year = parseInt(shortYear) > 50 ? `19${shortYear}` : `20${shortYear}`;
      
      const dayNum = parseInt(day);
      const monthNum = parseInt(month);
      
      if (dayNum >= 1 && dayNum <= 31 && monthNum >= 1 && monthNum <= 12) {
        const isoDate = `${year}-${month}-${day}`;
        const date = new Date(isoDate);
        if (isValid(date) && date.getDate() === dayNum && date.getMonth() + 1 === monthNum) {
          return isoDate;
        }
      }
    }
    
    // Formato italiano: gg/mm/aaaa, gg-mm-aaaa, gg.mm.aaaa
    const italianMatch = cleanStr.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
    if (italianMatch) {
      const [, day, month, year] = italianMatch;
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      const isoDate = `${year}-${paddedMonth}-${paddedDay}`;
      const date = new Date(isoDate);
      // Verifica che la data sia valida (es. 31/02/2024 non è valido)
      if (isValid(date) && date.getDate() === parseInt(paddedDay)) {
        return isoDate;
      }
    }
    
    // Formato italiano con anno a 2 cifre: gg/mm/aa
    const shortYearMatch = cleanStr.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2})$/);
    if (shortYearMatch) {
      const [, day, month, shortYear] = shortYearMatch;
      const year = parseInt(shortYear) > 50 ? `19${shortYear}` : `20${shortYear}`;
      const paddedDay = day.padStart(2, '0');
      const paddedMonth = month.padStart(2, '0');
      const isoDate = `${year}-${paddedMonth}-${paddedDay}`;
      const date = new Date(isoDate);
      if (isValid(date) && date.getDate() === parseInt(paddedDay)) {
        return isoDate;
      }
    }
    
    return null;
  };

  // Funzione per formattare una data ISO in formato italiano
  const formatToItalian = (isoDate) => {
    if (!isoDate) return "";
    try {
      const date = new Date(isoDate);
      if (isValid(date)) {
        return format(date, "dd/MM/yyyy", { locale: it });
      }
    } catch {
      // Ignora errori di parsing
    }
    return "";
  };

  // Sincronizza il valore testuale quando cambia il valore esterno
  React.useEffect(() => {
    setTextValue(formatToItalian(value));
  }, [value]);

  // Gestisce il cambio del campo di testo - Formattazione PASSIVA con slash automatici
  const handleTextChange = (e) => {
    const rawInput = e.target.value;
    const prevValue = textValue;
    
    // Se l'utente sta cancellando, lascia fare senza interferire
    if (rawInput.length < prevValue.length) {
      setTextValue(rawInput);
      if (rawInput === "") {
        onChange?.("");
      }
      return;
    }
    
    // Estrai solo le cifre dall'input corrente
    const currentDigits = rawInput.replace(/[^\d]/g, '');
    
    // Se l'input contiene separatori manuali (-, .), lascia l'utente gestire
    if (/[\-\.]/.test(rawInput)) {
      setTextValue(rawInput);
      // Prova a parsare
      if (currentDigits.length === 8) {
        const parsed = parseDate(rawInput);
        if (parsed) {
          onChange?.(parsed);
        }
      }
      return;
    }
    
    // Costruisci la stringa formattata con slash automatici
    let formatted = '';
    for (let i = 0; i < currentDigits.length && i < 8; i++) {
      // Aggiungi slash PRIMA del 3° carattere (indice 2) e PRIMA del 5° (indice 4)
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += currentDigits[i];
    }
    
    setTextValue(formatted);
    
    // Prova a parsare quando abbiamo 8 cifre
    if (currentDigits.length === 8) {
      const parsed = parseDate(formatted);
      if (parsed) {
        onChange?.(parsed);
      }
    } else if (formatted === "") {
      onChange?.("");
    }
  };

  // Gestisce l'incolla - formatta immediatamente solo se è una data valida completa
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").trim();
    const parsed = parseDate(pastedText);
    
    if (parsed) {
      // Data valida: formatta e salva
      setTextValue(formatToItalian(parsed));
      onChange?.(parsed);
    } else {
      // Non è una data valida: mostra il testo incollato senza modifiche
      setTextValue(pastedText);
    }
  };

  // Gestisce la perdita del focus - valida e formatta SOLO se completo
  const handleBlur = () => {
    if (textValue) {
      const parsed = parseDate(textValue);
      if (parsed) {
        // Data valida: formatta nel formato italiano
        setTextValue(formatToItalian(parsed));
        onChange?.(parsed);
      } else if (value) {
        // Testo non valido ma c'era un valore precedente: ripristinalo
        setTextValue(formatToItalian(value));
      }
      // Se il testo non è valido e non c'era valore precedente, lascia il testo così com'è
    }
  };

  // Gestisce la selezione dal calendario
  const handleCalendarSelect = (date) => {
    if (date) {
      const isoDate = format(date, "yyyy-MM-dd");
      setTextValue(formatToItalian(isoDate));
      onChange?.(isoDate);
    }
    setIsCalendarOpen(false);
  };

  // Converte il valore ISO in oggetto Date per il calendario
  const calendarDate = React.useMemo(() => {
    if (value) {
      const date = new Date(value);
      return isValid(date) ? date : undefined;
    }
    return undefined;
  }, [value]);

  return (
    <div className={cn("relative flex gap-1", className)}>
      <Input
        ref={ref}
        type="text"
        value={textValue}
        onChange={handleTextChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1"
        {...props}
      />
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            className="h-9 w-9 shrink-0"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={calendarDate}
            onSelect={handleCalendarSelect}
            initialFocus
            locale={it}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});

DateInput.displayName = "DateInput";

export { DateInput };
